import React, { useState, useRef, useEffect } from 'react';
import { API_URL } from '../services/api';
import axios from 'axios';
import {
  Box,
  IconButton,
  Typography,
  Slider,
  Paper,
  Collapse,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import RadioIcon from '@mui/icons-material/Radio';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

const RadioPlayer: React.FC = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(70);
  const [isMuted, setIsMuted] = useState(false);
  const [isExpanded, setIsExpanded] = useState(true);
  const [nowPlaying, setNowPlaying] = useState('EfectoFM 89.0 MHz');
  const audioRef = useRef<HTMLAudioElement>(null);

  // Determinar la URL del stream según el protocolo
  const getStreamUrl = () => {
    if (window.location.protocol === 'https:') {
      // En HTTPS, usar el proxy del backend Node.js
      return '/api/stream';
    } else {
      // En HTTP, también podemos usar el proxy para consistencia
      // o el stream directo si prefieres
      return `${API_URL}/api/stream`;
    }
  };

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume / 100;
    }
  }, [volume]);

  useEffect(() => {
    // Fetch now playing info
    const fetchNowPlaying = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/nowplaying`);
        if (response.data.success) {
          setNowPlaying(response.data.nowPlaying);
        }
      } catch (error) {
        console.error('Error fetching now playing:', error);
      }
    };

    // Fetch immediately
    fetchNowPlaying();

    // Update every 30 seconds
    const interval = setInterval(fetchNowPlaying, 30000);

    return () => clearInterval(interval);
  }, []);

  const togglePlay = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleVolumeChange = (_event: Event, newValue: number | number[]) => {
    const volumeValue = newValue as number;
    setVolume(volumeValue);
    setIsMuted(volumeValue === 0);
  };

  const toggleMute = () => {
    if (audioRef.current) {
      if (isMuted) {
        audioRef.current.volume = volume / 100;
        setIsMuted(false);
      } else {
        audioRef.current.volume = 0;
        setIsMuted(true);
      }
    }
  };

  return (
    <>
      {/* Logo flotante sobre el player */}
      <Box
        sx={{
          position: 'fixed',
          bottom: 80,
          right: 20,
          zIndex: 1199,
          display: { xs: 'none', md: 'block' },
        }}
      >
        <img
          src="/logos/efectofm_500.png"
          alt="EfectoFM Logo"
          style={{
            width: '120px',
            height: 'auto',
            opacity: 0.95,
            filter: 'drop-shadow(0 4px 6px rgba(0, 0, 0, 0.5))',
          }}
        />
      </Box>

      <Paper
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          bgcolor: 'rgba(0, 0, 0, 0.95)',
          backdropFilter: 'blur(10px)',
          borderTop: '2px solid',
          borderColor: 'primary.main',
          zIndex: 1200,
        }}
        elevation={8}
      >
        <audio
          ref={audioRef}
          src={getStreamUrl()}
          preload="none"
        />

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            py: 1,
          }}
        >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
          <IconButton
            onClick={togglePlay}
            sx={{
              bgcolor: 'primary.main',
              color: 'white',
              '&:hover': {
                bgcolor: 'primary.dark',
              },
            }}
          >
            {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
          </IconButton>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <RadioIcon color="primary" />
            <Box>
              <Typography variant="subtitle2" fontWeight={700}>
                EFECTO<span style={{ color: '#DC143C' }}>FM</span>
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {isPlaying ? 'En vivo - 89.0 MHz' : 'Haz clic para reproducir'}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Ahora suena en el centro */}
        <Box sx={{
          flex: 2,
          textAlign: 'center',
          px: 2,
          display: { xs: isExpanded ? 'block' : 'none', md: 'block' }
        }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
            Ahora suena:
          </Typography>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 600,
              color: 'primary.main',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}
          >
            {nowPlaying}
          </Typography>
        </Box>

        <Collapse in={isExpanded} orientation="horizontal">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 200 }}>
            <IconButton onClick={toggleMute} size="small">
              {isMuted ? <VolumeOffIcon /> : <VolumeUpIcon />}
            </IconButton>
            <Slider
              value={isMuted ? 0 : volume}
              onChange={handleVolumeChange}
              aria-labelledby="volume-slider"
              sx={{ width: 100 }}
            />
            <Typography variant="caption" sx={{ minWidth: 30 }}>
              {isMuted ? '0' : volume}%
            </Typography>
          </Box>
        </Collapse>

        <IconButton
          onClick={() => setIsExpanded(!isExpanded)}
          size="small"
          sx={{ ml: 1 }}
        >
          {isExpanded ? <KeyboardArrowDownIcon /> : <KeyboardArrowUpIcon />}
        </IconButton>
      </Box>
    </Paper>
    </>
  );
};

export default RadioPlayer;